import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import MetricCard from '@dashboard/shared/MetricCard';
import RevenueChart from '@dashboard/shared/RevenueChart';
import StatusBadge from '@dashboard/shared/StatusBadge';
import DateRangeSelector, { getFilterDates } from '@dashboard/shared/DateRangeSelector';
import type { DateFilter } from '@dashboard/shared/DateRangeSelector';
import { timeAgo, formatElapsed } from '@dashboard/shared/timeAgo';
import { downloadCsv } from '@dashboard/shared/csvExport';
import StatusDistribution from './StatusDistribution';
import FailureBreakdown from './FailureBreakdown';
import PendingOrders from './PendingOrders';
import WebhookHealth from './WebhookHealth';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import type { Order, OrderStatus } from '@shared/types';

function isCryptoOrder(o: Order) {
  return o.provider === 'crypto' || o.payment_provider === 'crypto';
}

function calcAvgSettlement(orders: Order[]): string {
  const settled = orders.filter(
    (o) => o.status === 'completed' && o.timeline && o.timeline.length >= 2,
  );
  if (settled.length === 0) return '--';
  let totalMs = 0;
  for (const o of settled) {
    const first = new Date(o.timeline![0].timestamp).getTime();
    const last = new Date(o.timeline![o.timeline!.length - 1].timestamp).getTime();
    totalMs += last - first;
  }
  return formatElapsed(totalMs / settled.length);
}

export default function CryptoOverview() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });

  useEffect(() => {
    async function load() {
      const { start, end } = getFilterDates(dateFilter);
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (start) query = query.gte('created_at', start);
      if (end) query = query.lte('created_at', end);

      const { data } = await query;
      if (data) {
        setOrders((data as unknown as Order[]).filter(isCryptoOrder));
      }
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel('crypto-orders-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        const incoming = payload.new as unknown as Order;
        if (!isCryptoOrder(incoming)) return;

        if (payload.eventType === 'INSERT') {
          setOrders((prev) => [incoming, ...prev].slice(0, 500));
        } else if (payload.eventType === 'UPDATE') {
          setOrders((prev) =>
            prev.map((o) => (o.id === incoming.id ? incoming : o))
          );
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, dateFilter]);

  const recentOrders = orders.slice(0, 10);

  const todayOrders = orders.filter(
    (o) => new Date(o.created_at).toDateString() === new Date().toDateString()
  );
  const todayRevenue = todayOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);
  const completedCount = todayOrders.filter((o) => o.status === 'completed').length;
  const successRate = todayOrders.length > 0
    ? ((completedCount / todayOrders.length) * 100).toFixed(0)
    : '0';
  const refundedAmount = orders
    .filter((o) => o.status === 'refunded')
    .reduce((sum, o) => sum + o.expected_amount, 0);

  const avgSettlement = calcAvgSettlement(orders);

  const chartData = orders
    .filter((o) => o.status === 'completed')
    .reduce<{ date: string; revenue: number }[]>((acc, o) => {
      const date = new Date(o.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const existing = acc.find((d) => d.date === date);
      if (existing) {
        existing.revenue += o.verified_amount || o.expected_amount;
      } else {
        acc.push({ date, revenue: o.verified_amount || o.expected_amount });
      }
      return acc;
    }, [])
    .reverse();

  const handleExportCsv = () => {
    const headers = ['ID', 'Status', 'Amount', 'Currency', 'Provider', 'Created'];
    const rows = orders.map((o) => [
      o.id,
      o.status,
      o.expected_amount.toFixed(2),
      o.currency || 'USD',
      o.provider || o.payment_provider || '',
      new Date(o.created_at).toISOString(),
    ]);
    downloadCsv(headers, rows, `crypto-orders-${dateFilter.range}`);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Crypto Overview</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCsv}
            disabled={orders.length === 0}
            title="Export CSV"
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            <Download size={14} />
          </button>
          <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Today's Revenue" value={`$${todayRevenue.toFixed(2)}`} />
        <MetricCard label="Today's Orders" value={String(todayOrders.length)} />
        <MetricCard label="Success Rate" value={`${successRate}%`} />
        <MetricCard label="Avg Settlement" value={avgSettlement} />
        {refundedAmount > 0 && (
          <MetricCard label="Refund Volume" value={`-$${refundedAmount.toFixed(2)}`} />
        )}
      </div>

      <WebhookHealth />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6 mt-6">
        <StatusDistribution orders={orders} />
        <PendingOrders orders={orders} />
        <FailureBreakdown orders={orders} />
      </div>

      <div className="mb-6">
        <RevenueChart data={chartData} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
            Recent Crypto Orders
          </h2>
          <Link
            to="/dashboard/crypto/orders"
            className="font-mono text-xs transition-colors"
            style={{ color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            View all
          </Link>
        </div>

        {loading ? (
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
        ) : recentOrders.length === 0 ? (
          <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <div className="text-3xl mb-3 opacity-30">&#x25C7;</div>
            <p className="font-sans text-sm mb-1" style={{ color: 'var(--dash-text-muted)' }}>No crypto orders yet</p>
            <p className="font-sans text-xs" style={{ color: 'var(--dash-text-faint)' }}>
              Orders will appear here once customers complete their first crypto payment.
            </p>
          </div>
        ) : (
          <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Order</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Status</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, i) => (
                  <tr
                    key={order.id}
                    className="transition-colors"
                    style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/dashboard/crypto/orders/${order.id}`}
                        className="font-mono text-xs transition-colors"
                        style={{ color: 'var(--dash-text-tertiary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                      >
                        {order.id.slice(0, 8)}...
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      ${order.expected_amount.toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={order.status as OrderStatus} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {timeAgo(order.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
